/* eslint-disable no-console -- intentional [sync] diagnostics; lifecycle
   events here go through console.info so they're visible in dev tools
   when debugging cross-device sync drift. */
import type { CardTag, Trip } from '@my-fullstack-app/itinerary-types';
import { useItineraryStore } from '../store/itinerary-store';
import { getAccessToken } from './auth-store';
import { Debouncer, Throttler } from './sync-primitives';

/**
 * Walk a trip and extract every entity-level note into a Record<id,string>.
 * Used on hydrate so the server's inline notes (Day/Transport/Custom/Place)
 * end up in the client's notesMap where the UI reads from.
 *
 * Returns a fresh map; callers merge it into the existing notesMap so locally
 * edited notes (already in the store) win when the same id is on both sides.
 */
function extractInlineNotes(trip: Trip): Record<string, string> {
  const out: Record<string, string> = {};
  if (trip.notes) out[trip.id] = trip.notes;
  for (const day of trip.days) {
    if (day.notes) out[day.id] = day.notes;
    for (const place of day.places) {
      if (place.notes) out[place.id] = place.notes;
    }
    for (const transport of day.transports ?? []) {
      if (transport.notes) out[transport.id] = transport.notes;
    }
    for (const custom of day.customs ?? []) {
      if (custom.notes) out[custom.id] = custom.notes;
    }
    // Legacy day-scoped journeys (ADR-0043 v1) — migrated up to
    // trip.journeys at hydrate time but might still be on the wire
    // from older payloads.
    for (const journey of day.journeys ?? []) {
      if (journey.notes) out[journey.id] = journey.notes;
    }
  }
  for (const stay of trip.stays ?? []) {
    if (stay.notes) out[stay.id] = stay.notes;
  }
  for (const journey of trip.journeys ?? []) {
    if (journey.notes) out[journey.id] = journey.notes;
  }
  for (const place of trip.wishlist ?? []) {
    if (place.notes) out[place.id] = place.notes;
  }
  return out;
}

/**
 * Strip inline notes from a trip after they've been moved to notesMap.
 * Local store keeps notes only in notesMap; inline fields stay undefined
 * until we serialize for the wire (see injectInlineNotes).
 */
function stripInlineNotes(trip: Trip): Trip {
  const strip = <T extends { notes?: string | undefined }>({ notes: _n, ...rest }: T): T =>
    rest as T;
  return {
    ...trip,
    days: trip.days.map((day) => ({
      ...strip(day),
      places: day.places.map(strip),
      transports: (day.transports ?? []).map(strip),
      customs: (day.customs ?? []).map(strip),
      ...(day.journeys ? { journeys: day.journeys.map(strip) } : {}),
    })),
    stays: (trip.stays ?? []).map(strip),
    journeys: (trip.journeys ?? []).map(strip),
    // Conditional spread (not `?? []`) so an absent wishlist stays
    // absent — preserves mergeServerTrip's `server ?? local` fallback
    // for the wishlist collection (a forced `[]` would wipe a local
    // wishlist when the server omits the field).
    ...(trip.wishlist ? { wishlist: trip.wishlist.map(strip) } : {}),
  };
}


function injectInlineNotes(trip: Trip, notesMap: Record<string, string>): Trip {
  // Inline a note onto an entity only when notesMap actually has one
  // for it — otherwise leave the field absent so EOPT-typed shapes
  // round-trip cleanly. Same idea as stripInlineNotes inverted.
  const withNote = <T extends { id: string; notes?: string | undefined }>(x: T): T => {
    const note = notesMap[x.id];
    return note !== undefined ? { ...x, notes: note } : x;
  };
  return {
    ...withNote(trip),
    days: trip.days.map((day) => ({
      ...withNote(day),
      places: day.places.map(withNote),
      transports: (day.transports ?? []).map(withNote),
      customs: (day.customs ?? []).map(withNote),
    })),
    stays: (trip.stays ?? []).map(withNote),
    journeys: (trip.journeys ?? []).map(withNote),
    ...(trip.wishlist ? { wishlist: trip.wishlist.map(withNote) } : {}),
  };
}

export type TripField = 'name' | 'destination' | 'startDate' | 'endDate' | 'preferences' | 'days' | 'notes' | 'tags' | 'heroPlaceId' | 'references' | 'stays' | 'journeys' | 'wishlist' | 'participants' | 'categories';
export type SyncStatus = 'idle' | 'syncing' | 'error';

export interface DirtyEntry {
  fields: TripField[];
  isNew: boolean;
  retryCount: number;
  retryAt: number | null;
  /** True when scheduleRetry has hit the BACKOFF ceiling — the manager has
   *  stopped auto-retrying and is waiting on the user. Read by sync
   *  indicators to render "Sync stopped" instead of the generic "Sync error". */
  retryExhausted?: boolean | undefined;
}

export interface HydrateOptions {
  /** Bypass the throttle window. Used by initial boot hydration. */
  force?: boolean | undefined;
}

export type HydrateResult =
  | { upserted: number; skippedUnchanged: number; skipped?: undefined | undefined}
  | { skipped: 'throttled' | 'error' | 'no-api' | 'unauthenticated'; upserted?: undefined | undefined; skippedUnchanged?: undefined | undefined};

export interface SyncApi {
  createTrip: (trip: Trip) => Promise<Trip>;
  updateTrip: (
    tripId: string,
    updates: Partial<Pick<Trip, 'name' | 'destination' | 'startDate' | 'endDate' | 'preferences' | 'days' | 'notes' | 'tags' | 'heroPlaceId' | 'references' | 'stays' | 'journeys' | 'wishlist' | 'participants' | 'categories'>>
  ) => Promise<Trip>;
  deleteTrip: (tripId: string) => Promise<void>;
  listTrips: () => Promise<{
    owned: Array<{ trip: Trip; role: string }>;
    shared: Array<{ trip: Trip; role: string }>;
  }>;
}

const ALL_FIELDS: TripField[] = ['name', 'destination', 'startDate', 'endDate', 'preferences', 'days', 'notes', 'tags', 'references', 'stays', 'journeys', 'wishlist', 'participants', 'categories'];

/** Field-level merge: keep local value for dirty fields, take server value for clean fields.
 *
 *  Defensive note for `stays` and `journeys`: if the server didn't
 *  carry the field at all (`undefined` — e.g. a payload from before
 *  the field was supported, or from a legacy trip never re-flushed),
 *  prefer local. Without this fallback, an unrelated dirty edit
 *  (`name`) would silently destroy locally-loaded journeys / stays.
 *  We use `??` so an explicit empty array from the server is still
 *  honored — only `undefined` falls back. */
export function mergeServerTrip(local: Trip, server: Trip, dirtyFields: TripField[]): Trip {
  const dirty = new Set(dirtyFields);
  // Pick local-or-server per field; we use conditional spreads for
  // optional fields so EOPT-typed Trip stays a clean shape (absent
  // fields stay absent rather than getting explicit `undefined`).
  const pickOpt = <K extends keyof Trip>(field: K): Trip[K] | undefined => {
    if (dirty.has(field as TripField)) return local[field];
    // For collection-shaped fields the server may legitimately
    // omit them (legacy payloads, never-flushed trips); fall back to local
    // so an unrelated dirty edit can't destroy local-only state.
    const COLLECTION = new Set<keyof Trip>([
      'stays', 'journeys', 'wishlist', 'participants', 'categories',
    ]);
    if (COLLECTION.has(field)) return (server[field] ?? local[field]);
    return server[field];
  };
  const opt = <K extends keyof Trip>(field: K) => {
    const v = pickOpt(field);
    return v !== undefined ? ({ [field]: v } as Pick<Trip, K>) : ({} as Pick<Trip, K>);
  };
  return {
    id: local.id,
    name: dirty.has('name') ? local.name : server.name,
    destination: dirty.has('destination') ? local.destination : server.destination,
    startDate: dirty.has('startDate') ? local.startDate : server.startDate,
    endDate: dirty.has('endDate') ? local.endDate : server.endDate,
    days: dirty.has('days') ? local.days : server.days,
    ...opt('preferences'),
    ...opt('notes'),
    ...opt('tags'),
    ...opt('heroPlaceId'),
    ...opt('references'),
    ...opt('stays'),
    ...opt('journeys'),
    ...opt('wishlist'),
    ...opt('participants'),
    ...opt('categories'),
    // Pull the server timestamp through, falling back to local so a
    // server payload without `updatedAt` (legacy / unsupported field)
    // can't blow the field away. Local dirty fields will get a fresh
    // `updatedAt` from the server on the next outbound flush.
    ...((server.updatedAt ?? local.updatedAt) !== undefined
      ? { updatedAt: server.updatedAt ?? local.updatedAt }
      : {}),
  };
}

export class SyncManager {
  /** Minimum time between hydrations from any non-`force` trigger. */
  private static readonly HYDRATE_THROTTLE_MS = 10_000;
  private static instance: SyncManager | null = null;
  private api: SyncApi | null = null;
  private flushDebouncer = new Debouncer(2000, () => this.flush());
  private hydrateThrottler = new Throttler(SyncManager.HYDRATE_THROTTLE_MS);
  private status: SyncStatus = 'idle';
  private statusListeners: Array<(s: SyncStatus) => void> = [];
  /** Listeners notified after a SUCCESSFUL hydrate (not on throttled /
   *  errored hydrates). Separate from `statusListeners` because status
   *  doesn't move on a no-op hydrate (`skippedUnchanged > 0`) and the
   *  trips-list "Updated Xm ago" pill still needs to refresh. */
  private hydrateListeners: Array<(at: number) => void> = [];

  static getInstance(): SyncManager {
    SyncManager.instance ??= new SyncManager();
    return SyncManager.instance;
  }

  /** Inject tRPC API functions. Called once from SyncManagerInit component. */
  init(api: SyncApi): void {
    this.api = api;
    this.startRetryTick();
  }

  /** Re-runs `flush()` every minute so dirty entries scheduled for retry get
   *  picked up even when the user makes no further edits. Idempotent. */
  private retryTimer: ReturnType<typeof setInterval> | null = null;
  private static readonly RETRY_TICK_MS = 60_000;
  private startRetryTick(): void {
    if (this.retryTimer) return;
    this.retryTimer = setInterval(() => {
      // Skip while signed out — we know writes will 401 until the user
      // re-auths. The next isAuthenticated transition will trigger a flush.
      if (!getAccessToken()) return;
      const dirty = useItineraryStore.getState().dirtyTrips as Record<string, DirtyEntry>;
      const deletes = useItineraryStore.getState().pendingDeletes;
      const hasWork =
        Object.values(dirty).some((e) => e.retryAt === null || e.retryAt <= Date.now()) ||
        deletes.length > 0;
      if (hasWork) void this.flush();
    }, SyncManager.RETRY_TICK_MS);
    // Don't keep the Node event loop alive solely for this timer (matters
    // for jest, scripts, and SSR — no-op on RN/web).
    (this.retryTimer as { unref?: (() => void) | undefined}).unref?.();
  }

  /** Demo trips are local-only and must never enter the dirty/delete
   *  queues, regardless of where they were marked. Belt-and-braces guard
   *  on every mark surface; callers (importTrips) also skip the call. */
  private isDemoTrip(tripId: string): boolean {
    return useItineraryStore.getState().trips.some(
      (t) => t.id === tripId && t.isDemo === true,
    );
  }

  /** Called by Zustand store after every mutation. */
  markDirty(tripId: string, fields: TripField[]): void {
    if (this.isDemoTrip(tripId)) return;
    const store = useItineraryStore.getState();
    const existing = store.dirtyTrips[tripId];
    const mergedFields = existing
      ? Array.from(new Set([...existing.fields, ...fields]))
      : fields;
    store.setDirtyEntry(tripId, {
      fields: mergedFields,
      isNew: existing?.isNew ?? false,
      retryCount: existing?.retryCount ?? 0,
      retryAt: existing?.retryAt ?? null,
    });
    this.debounceFlush();
  }

  /** Called by Zustand store after createTrip / duplicateTrip / importTrips. */
  markNew(tripId: string): void {
    if (this.isDemoTrip(tripId)) return;
    const store = useItineraryStore.getState();
    store.setDirtyEntry(tripId, {
      fields: [...ALL_FIELDS],
      isNew: true,
      retryCount: 0,
      retryAt: null,
    });
    this.debounceFlush();
  }

  /** Called by Zustand store after deleteTrip. The slice itself filters
   *  demo trips out before calling — by the time we're here, the trip is
   *  already gone from state so we can't re-check `isDemo` ourselves. */
  markDeleted(tripId: string): void {
    const store = useItineraryStore.getState();
    store.addPendingDelete(tripId);
    store.clearDirtyEntry(tripId);
    this.debounceFlush();
  }

  getStatus(): SyncStatus {
    return this.status;
  }

  onStatusChange(listener: (s: SyncStatus) => void): () => void {
    this.statusListeners.push(listener);
    return () => {
      this.statusListeners = this.statusListeners.filter((l) => l !== listener);
    };
  }

  /** Epoch-ms of the last successful hydrate. `null` until the first one
   *  completes — surfaces "Updated Xm ago" on the trips list. Distinguishing
   *  null from a real timestamp avoids the "Updated 56 years ago" trap where
   *  pre-hydrate `0` got passed through `now - lastHydrateAt`. */
  getLastHydrateAt(): number | null {
    return this.hydrateThrottler.getLastAt();
  }

  /** Subscribe to successful-hydrate events. The listener receives the
   *  timestamp the hydrate finished. Useful for re-rendering relative-time
   *  UI without coupling it to status transitions. */
  onHydrate(listener: (at: number) => void): () => void {
    this.hydrateListeners.push(listener);
    return () => {
      this.hydrateListeners = this.hydrateListeners.filter((l) => l !== listener);
    };
  }

  /**
   * Flush immediately. Used for two distinct paths:
   *  - Lifecycle bookend (page hide / app background / sign-out). Just push
   *    whatever's queued.
   *  - User-initiated retry (Sync error pill press). Clears the BACKOFF
   *    `retryAt` on every dirty entry so the manual retry actually attempts
   *    the API call instead of silently filtering all entries out.
   */
  async flushImmediate(): Promise<void> {
    // Don't fire mutations against a protectedProcedure without a token.
    // The biggest caller hit by this is the subscribeAuthExpired handler
    // in AuthContext: clearAuth runs first, wiping the in-memory token,
    // then switchToAnonymous calls flushImmediate. Pre-gate, every
    // dirty trip caused a 401 + a synthetic 'error' status flap. The
    // dirty queue stays intact; SyncManagerInit's [isAuthenticated]
    // effect drains it on next sign-in. Mirrors the equivalent guard
    // in UserPrefsSync.flushImmediate.
    if (!getAccessToken()) return;
    this.flushDebouncer.cancel();
    const store = useItineraryStore.getState();
    const dirty = store.dirtyTrips as Record<string, DirtyEntry>;
    for (const [id, entry] of Object.entries(dirty)) {
      // Clear both the BACKOFF window AND the exhausted flag so a manual
      // retry actually attempts the API call, and a successful one can
      // unstick the entry from "Sync stopped" state.
      if (entry.retryAt !== null || entry.retryExhausted) {
        store.setDirtyEntry(id, {
          ...entry,
          retryAt: null,
          retryExhausted: false,
          retryCount: entry.retryExhausted ? 0 : entry.retryCount,
        });
      }
    }
    await this.flush();
  }

  /** Last error message captured from a failed flush or hydrate. Surfaced
   *  via sync indicators (SyncDot / SyncFreshnessPill) so the user can see
   *  WHY syncing keeps failing. */
  private lastErrorMessage: string | null = null;
  getLastError(): string | null {
    return this.lastErrorMessage;
  }
  private recordError(context: string, e: unknown): void {
    const msg = e instanceof Error ? e.message : String(e);
    this.lastErrorMessage = `${context}: ${msg}`;
    console.error(`[sync] ${context}`, e);
  }

  private hydrateInFlight: Promise<HydrateResult> | null = null;

  /**
   * Fetch server trips and merge into local store. Called on app start, on
   * screen focus, on `AppState` resume, and on a 30s polling tick while a
   * trip is open.
   *
   * Throttled to once per `HYDRATE_THROTTLE_MS` unless `{ force: true }` is
   * passed. Concurrent callers receive the same in-flight promise.
   *
   * Trips whose server `updatedAt` matches the local cache are skipped to
   * avoid unnecessary store mutations and re-renders.
   */
  async hydrateFromServer(opts: HydrateOptions = {}): Promise<HydrateResult> {
    const api = this.api;
    if (!api) return { skipped: 'no-api' };

    // Auth-gate, mirroring flush()/flushImmediate()/the retry-tick.
    // listTrips is a protectedProcedure: firing it without a token 401s,
    // and the tRPC fetch wrapper escalates an unrecoverable 401 to
    // emitAuthExpired. For an ANONYMOUS user (who has no token by design)
    // that tears the session down on every poll/focus tick —
    // switchToAnonymous wipes consent (re-showing the GDPR sheet) and
    // rehydrates the persist scope (the page "refreshes"), while this
    // catch flips status to 'error' (the sync-error pill on /trips and
    // the demo trip). Bailing here before the network call breaks the
    // loop; there's nothing to hydrate for an anonymous user anyway.
    if (!getAccessToken()) return { skipped: 'unauthenticated' };

    if (this.hydrateThrottler.shouldSkip(opts.force)) {
      return { skipped: 'throttled' };
    }
    if (this.hydrateInFlight) return this.hydrateInFlight;

    this.hydrateInFlight = (async (): Promise<HydrateResult> => {
      console.info('[sync] hydrate start');
      try {
        const result = await api.listTrips();
        const store = useItineraryStore.getState();
        const allServerTrips = [
          ...result.owned.map((r) => r.trip),
          ...result.shared.map((r) => r.trip),
        ];
        let upserted = 0;
        let skippedUnchanged = 0;
        // Aggregate notesMap + tagsMap deltas across all server trips so
        // we can patch the store once at the end (one set call instead of N).
        const serverNotes: Record<string, string> = {};
        const serverTags: Record<string, CardTag> = {};
        const pendingDeleteIds = new Set<string>(store.pendingDeletes);
        for (const serverTrip of allServerTrips) {
          // Don't resurrect a trip the user already deleted locally — the
          // delete is queued in pendingDeletes and will be sent on the
          // next flush. Without this skip, a hydrate (poll tick, screen
          // focus, AppState resume) racing the delete flush re-upserts
          // the server copy and the trip "reappears" in the list.
          if (pendingDeleteIds.has(serverTrip.id)) continue;
          // Lift inline notes (Day/Place/Transport/Custom) into the delta map
          // so the new design's notesMap-based UI sees them on a fresh
          // browser tab where AsyncStorage is empty.
          Object.assign(serverNotes, extractInlineNotes(serverTrip));
          // Same for tags — Trip.tags is the wire-level Record<entityId, CardTag>;
          // local truth is the tagsMap slice. Merge across trips.
          if (serverTrip.tags) Object.assign(serverTags, serverTrip.tags);
          const cleaned = stripInlineNotes(serverTrip);
          const local = store.trips.find((t) => t.id === cleaned.id);
          if (!local) {
            store.upsertTrip(cleaned);
            upserted += 1;
            continue;
          }
          const dirtyEntry = store.dirtyTrips[cleaned.id];
          if (
            local.updatedAt &&
            cleaned.updatedAt &&
            local.updatedAt === cleaned.updatedAt &&
            !dirtyEntry
          ) {
            skippedUnchanged += 1;
            continue;
          }
          const merged = dirtyEntry
            ? mergeServerTrip(local, cleaned, dirtyEntry.fields as TripField[])
            : cleaned;
          store.upsertTrip(merged);
          upserted += 1;
          // Reconciliation: a trip the server has acknowledged cannot be
          // `isNew`. This happens when an earlier createTrip succeeded
          // remotely but the response never reached the client (network
          // blip, tab close mid-flight). Leaving `isNew: true` here would
          // loop forever — every retry calls createTrip again, the server
          // counts the existing row, and the Free-tier cap fires.
          if (dirtyEntry?.isNew) {
            if (dirtyEntry.fields.length > 0) {
              store.setDirtyEntry(cleaned.id, {
                ...dirtyEntry,
                isNew: false,
                retryAt: null,
                retryCount: 0,
                retryExhausted: false,
              });
            } else {
              store.clearDirtyEntry(cleaned.id);
            }
          }
        }
        // Merge server notes/tags UNDER local edits — anything the user has
        // typed/set (already in the store) takes precedence over the server
        // copy via spread order.
        const haveNotes = Object.keys(serverNotes).length > 0;
        const haveTags = Object.keys(serverTags).length > 0;
        if (haveNotes || haveTags) {
          const local = useItineraryStore.getState();
          useItineraryStore.setState({
            ...(haveNotes && { notesMap: { ...serverNotes, ...local.notesMap } }),
            ...(haveTags && { tagsMap: { ...serverTags, ...local.tagsMap } }),
          });
        }
        // Update the throttle anchor only on success — failures should not
        // lock subsequent attempts out of the throttle window.
        this.hydrateThrottler.markFired();
        // markFired() just ran, so getLastAt() is guaranteed non-null —
        // assert for the listener signature without widening to `| null`.
        const firedAt = this.hydrateThrottler.getLastAt()!;
        for (const l of this.hydrateListeners) l(firedAt);
        console.info(`[sync] hydrate done (upserted=${upserted} skippedUnchanged=${skippedUnchanged})`);
        // A successful read doesn't tell us much about write health, but it
        // does mean the API is reachable; clear a sticky 'error' status so
        // the pill flips back to idle.
        if (this.status === 'error') {
          const stillDirty = Object.keys(useItineraryStore.getState().dirtyTrips).length > 0;
          if (!stillDirty) this.setStatus('idle');
        }
        return { upserted, skippedUnchanged };
      } catch (e) {
        this.recordError('hydrate failed', e);
        this.setStatus('error');
        return { skipped: 'error' };
      } finally {
        this.hydrateInFlight = null;
      }
    })();

    return this.hydrateInFlight;
  }

  private debounceFlush(): void {
    this.flushDebouncer.schedule();
  }

  private async flush(): Promise<void> {
    if (!this.api) return;
    // Central auth-gate. Three callers hit this path: flushImmediate
    // (which has its own pre-gate for clear-timer-then-skip-work),
    // the retry-tick (also has its own pre-gate), and the debounceFlush
    // timer (which calls `this.flush()` directly with NO pre-gate).
    // Without the gate here, a debounce timer armed before
    // subscribeAuthExpired's clearAuth would fire ~2s later against
    // a null token and 401 a dirty trip mutation. Centralising the
    // gate covers every entry point.
    if (!getAccessToken()) return;
    const store = useItineraryStore.getState();
    const dirtyEntries = Object.entries(store.dirtyTrips);
    const pendingDeletes = [...store.pendingDeletes];
    if (dirtyEntries.length === 0 && pendingDeletes.length === 0) return;

    console.info(`[sync] flush start (dirty=${dirtyEntries.length} deletes=${pendingDeletes.length})`);
    this.setStatus('syncing');

    for (const tripId of pendingDeletes) {
      try {
        await this.api.deleteTrip(tripId);
        store.removePendingDelete(tripId);
      } catch (e) {
        console.warn('[sync] delete failed', tripId, e);
        // Keep in pendingDeletes, will retry next flush
      }
    }

    const results = await Promise.allSettled(
      (dirtyEntries as Array<[string, DirtyEntry]>)
        .filter(([, entry]) => entry.retryAt === null || entry.retryAt <= Date.now())
        .map(([tripId, entry]) => this.syncTrip(tripId, entry))
    );

    const anyError = results.some((r) => r.status === 'rejected');
    const stillDirty = Object.keys(useItineraryStore.getState().dirtyTrips).length > 0;
    const stillDeletes = useItineraryStore.getState().pendingDeletes.length > 0;

    if (anyError || stillDirty || stillDeletes) {
      console.warn(`[sync] flush done with errors (stillDirty=${stillDirty} stillDeletes=${stillDeletes})`);
      this.setStatus('error');
    } else {
      console.info('[sync] flush done');
      this.setStatus('idle');
    }
  }

  private async syncTrip(tripId: string, entry: DirtyEntry): Promise<void> {
    if (!this.api) return;
    const store = useItineraryStore.getState();
    const localTrip = store.trips.find((t) => t.id === tripId);
    if (!localTrip) {
      store.clearDirtyEntry(tripId);
      return;
    }
    // Re-fold notesMap into inline note fields before serializing — local
    // store keeps notes only in notesMap, but the wire schema has them
    // inline on each entity (Day/Place/Transport/Custom).
    // Same idea for tags: local truth is tagsMap (a slice-level Record);
    // on the wire we ship as Trip.tags so cross-device sync sees them.
    // Scope to only this trip's entity IDs to avoid leaking other trips' tags.
    const tripWithNotes = injectInlineNotes(localTrip, store.notesMap);
    const tripEntityIds = new Set<string>([
      localTrip.id,
      ...localTrip.days.flatMap((d) => [
        d.id,
        ...d.places.map((p) => p.id),
        ...(d.transports ?? []).map((t) => t.id),
        ...(d.customs ?? []).map((c) => c.id),
      ]),
      ...(localTrip.stays ?? []).map((s) => s.id),
      ...(localTrip.journeys ?? []).map((j) => j.id),
      ...(localTrip.wishlist ?? []).map((p) => p.id),
    ]);
    const tripTags = Object.fromEntries(
      Object.entries(store.tagsMap).filter(([id]) => tripEntityIds.has(id)),
    );
    const trip: Trip = { ...tripWithNotes, tags: tripTags };

    try {
      let serverTrip: Trip | undefined;
      if (entry.isNew) {
        serverTrip = await this.api.createTrip(trip);
      } else {
        const updates: Partial<
          Pick<
            Trip,
            | 'name'
            | 'destination'
            | 'startDate'
            | 'endDate'
            | 'preferences'
            | 'days'
            | 'notes'
            | 'tags'
            | 'heroPlaceId'
            | 'references'
            | 'stays'
            | 'journeys'
            | 'wishlist'
            | 'participants'
            | 'categories'
          >
        > = {};
        for (const field of entry.fields) {
          (updates as Record<string, unknown>)[field] = trip[field];
        }
        serverTrip = await this.api.updateTrip(tripId, updates);
      }
      // Persist the server-canonical `updatedAt` back into the local cache so
      // the next inbound hydrate can correctly skip "we just wrote this" via
      // the updatedAt-equality short-circuit.
      if (serverTrip.updatedAt) {
        const cur = useItineraryStore.getState().trips.find((t) => t.id === tripId);
        if (cur) {
          useItineraryStore.getState().upsertTrip({ ...cur, updatedAt: serverTrip.updatedAt });
        }
      }
      store.clearDirtyEntry(tripId);
    } catch (e) {
      // 401 means our token is dead. The fetch wrapper already attempted a
      // refresh and `emitAuthExpired` was fired if that failed. Don't
      // schedule another BACKOFF retry — auth has to be restored first,
      // and the existing entry will be picked up by the post-re-auth flush.
      if (isUnauthorized(e)) {
        this.lastErrorMessage = 'Session expired — sign in to resume sync';
        console.warn('[sync] syncTrip got 401 for', tripId, '— pausing retries until re-auth');
        throw e;
      }
      this.recordError(`syncTrip failed for ${tripId}`, e);
      this.scheduleRetry(tripId, entry);
      throw e;
    }
  }

  private scheduleRetry(tripId: string, entry: DirtyEntry): void {
    const store = useItineraryStore.getState();
    const BACKOFF = [5_000, 15_000, 60_000];
    const nextRetryCount = entry.retryCount + 1;
    if (nextRetryCount > BACKOFF.length) {
      // Stop auto-retrying and flag the entry so the pill can surface a
      // distinct "Sync stopped" state. Manual retry / discard is the only
      // way out from here; flushImmediate clears retryExhausted on demand.
      console.warn('[sync] retry exhausted for', tripId);
      store.setDirtyEntry(tripId, {
        ...entry,
        retryAt: null,
        retryExhausted: true,
      });
      return;
    }
    const retryAt = Date.now() + BACKOFF[nextRetryCount - 1]!;
    console.info(`[sync] retry ${nextRetryCount}/${BACKOFF.length} for ${tripId} in ${BACKOFF[nextRetryCount - 1]}ms`);
    store.setDirtyEntry(tripId, {
      ...entry,
      retryCount: nextRetryCount,
      retryAt,
      retryExhausted: false,
    });
  }

  private setStatus(s: SyncStatus): void {
    this.status = s;
    if (s === 'idle') this.lastErrorMessage = null;
    for (const l of this.statusListeners) l(s);
  }

  /**
   * Drop every pending mutation (dirty entry + pending delete) and pull the
   * server's current truth into the local cache. Used by the "Discard
   * pending changes" affordance when the user is stuck on a non-recoverable
   * sync error (e.g. server-side validation). All local edits since the
   * last successful flush are lost — this is intentional, last-write-from-
   * server wins.
   */
  async discardPending(): Promise<void> {
    this.flushDebouncer.cancel();
    const store = useItineraryStore.getState();
    const dirtyIds = Object.keys(store.dirtyTrips);
    const pendingDeletes = [...(store.pendingDeletes)];
    for (const id of dirtyIds) store.clearDirtyEntry(id);
    for (const id of pendingDeletes) store.removePendingDelete(id);
    this.lastErrorMessage = null;
    this.setStatus('idle');
    await this.hydrateFromServer({ force: true });
  }

  /** For tests: reset singleton state. */
  static _reset(): void {
    if (SyncManager.instance?.retryTimer) {
      clearInterval(SyncManager.instance.retryTimer);
    }
    SyncManager.instance?.flushDebouncer.cancel();
    SyncManager.instance = null;
  }
}

/**
 * Best-effort detection of HTTP 401 from a thrown tRPC / fetch error. tRPC
 * wraps fetch errors in `TRPCClientError` with `data.httpStatus`; we also
 * fall back to a substring match for cases where the error shape differs
 * (network-level rejections, plain Error wrappers).
 */
function isUnauthorized(e: unknown): boolean {
  if (!e || typeof e !== 'object') return false;
  const err = e as { data?: { httpStatus?: number } | undefined; message?: string | undefined; cause?: { httpStatus?: number } | undefined};
  if (err.data?.httpStatus === 401) return true;
  if (err.cause?.httpStatus === 401) return true;
  return typeof err.message === 'string' && /\b401\b|UNAUTHORIZED/i.test(err.message);
}
